import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { AdminPanelSettingsOutlined, EngineeringOutlined, ReduceCapacityOutlined } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRemindersQuery } from "../../../../../../organization-reminder/hooks/useRemindersQuery";
import { filterRemindersByTarget } from "../../../../../../organization-reminder/helpers/filter-reminders-by-type.helper";
import ReminderCategoryCard from "./ReminderCategoryCard";
import { setReminderByCategory } from "../../../../../../../redux/features/jobCreateSlice";
import { colors } from "../../../../../../ui/colors";

export default function ManageReminders() {
  const dispatch = useDispatch();
  const selectedReminders = useSelector((state: any) => state.jobCreateStepper.selectedReminders);
  const [draftReminders, setDraftReminders] = useState(selectedReminders);
  const { data: reminders } = useRemindersQuery();

  const groupedReminders = useMemo(() => {
    const dataToFilter = Array.isArray(reminders) ? reminders : [];
    const onlyActive = dataToFilter.filter((reminder) => reminder.isActive);
    return filterRemindersByTarget(onlyActive);
  }, [reminders]);

  const handleToggleDraft = (category: string, reminder: any) => {
    setDraftReminders((prev) => ({
      ...prev,
      [category]: prev[category]?.uid === reminder.uid ? null : reminder,
    }));
  };

  const handleSaveToRedux = () => {
    Object.entries(draftReminders).forEach(([category, data]) => {
      dispatch(setReminderByCategory({ category, data }));
    });
  };

  const handleCancelChanges = () => {
    setDraftReminders(selectedReminders);
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, overflowY: "auto", width: "100%", px: 8, py: 4 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 3 }}>Reminders</Typography>

        <Stack gap={2}>
          {groupedReminders.admins?.length > 0 && (
            <ReminderCategoryCard
              title="Admins"
              icon={<AdminPanelSettingsOutlined />}
              reminders={groupedReminders.admins}
              onToggle={(uid) => {
                const reminder = groupedReminders.admins.find((r) => r.uid === uid);
                handleToggleDraft("admins", reminder);
              }}
              selectedId={draftReminders?.admins?.uid}
            />
          )}

          {groupedReminders.clients?.length > 0 && (
            <ReminderCategoryCard
              title="Client"
              icon={<ReduceCapacityOutlined />}
              reminders={groupedReminders.clients}
              onToggle={(uid) => {
                const reminder = groupedReminders.clients.find((r) => r.uid === uid);
                handleToggleDraft("clients", reminder);
              }}
              selectedId={draftReminders?.clients?.uid}
            />
          )}

          {groupedReminders.workforce?.length > 0 && (
            <ReminderCategoryCard
              title="Workforce"
              icon={<EngineeringOutlined />}
              reminders={groupedReminders.workforce}
              onToggle={(uid) => {
                const reminder = groupedReminders.workforce.find((r) => r.uid === uid);
                handleToggleDraft("workforce", reminder);
              }}
              selectedId={draftReminders?.workforce?.uid}
            />
          )}
        </Stack>
      </Box>

      {/* FOOTER PERSISTENTE */}
      <Divider sx={{ borderColor: colors.orange[100] }} />
      <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 2, bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
        <Button
          onClick={handleCancelChanges}
          variant="outlined"
          sx={{
            color: colors.gray[500],
            borderColor: colors.gray[300],
            textTransform: "none",
            "&:hover": { borderColor: colors.gray[500], bgcolor: colors.gray[50] },
          }}
        >
          Reset to Saved
        </Button>

        <Button
          onClick={handleSaveToRedux}
          variant="contained"
          disabled={JSON.stringify(draftReminders) === JSON.stringify(selectedReminders)}
          sx={{
            bgcolor: colors.orange.base,
            textTransform: "none",
            "&:hover": { bgcolor: colors.orange[700] },
            borderRadius: "8px",
            px: 4,
          }}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}
